import type { ReactNode } from "react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { OnboardingStepPane } from "@/onboarding/routes";
import { OnboardingProgress } from "./onboarding-progress";

interface StepLayoutProps {
  activePane: OnboardingStepPane;
  title: string;
  subtitle: string;
  children: ReactNode;
  /**
   * The step's Back and Continue. Owned by the shell so they sit at the bottom of the
   * page on every step, rather than under whichever column happens to be shorter.
   */
  nav?: ReactNode;
}

// One shell for every step: the progress bar, the heading, then the step's own content.
// Keeping it here means the bar cannot drift out of alignment between steps.
export const StepLayout = ({
  activePane,
  title,
  subtitle,
  children,
  nav,
}: StepLayoutProps) => (
  <Stack
    fullWidth
    align="center"
    className="min-h-[calc(100vh-67px)] bg-surface-secondary px-4 py-8"
  >
    <Stack gap="xl" align="stretch" fullWidth className="max-w-3xl">
      <OnboardingProgress activePane={activePane} />

      {/* The bar is a different kind of thing from the step itself, so it gets the wider
          gap above while the heading stays tight to its content. */}
      <Stack gap="lg" align="stretch" fullWidth>
        <Stack gap="xs" fullWidth>
          <Typography variant="titleW2">{title}</Typography>
          <Typography variant="bodyW1" textColor="secondary">
            {subtitle}
          </Typography>
        </Stack>

        {children}
      </Stack>

      {nav}
    </Stack>
  </Stack>
);
